import React, { useRef, useState } from 'react';
import { ExternalLink, RefreshCw, FileText, CheckCircle2, AlertTriangle, Upload, Loader2, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { apiDirect } from '../services/api';
import { appendAuthToken } from '../services/auth';


// Heuristic: any URL that's not on our domain and not already an
// internal pitch-deck/file URL is at risk of expiring. Airtable CDN
// URLs (v5.airtableusercontent.com, *.dl.airtableusercontent.com) are
// the canonical case but partner clouds (Dropbox short-lived links,
// Google Drive sharing tokens) hit the same problem.
const isExternalAtRiskUrl = (url: string | null): boolean => {
  if (!url) return false;
  if (url.includes('/api/v1/companies/') && url.includes('/pitch-deck/file')) {
    return false;
  }
  if (url.includes('airtableusercontent.com')) return true;
  if (url.includes('dropbox.com')) return true;
  if (url.includes('drive.google.com')) return true;
  // Anything else off-domain is suspicious but not flagged automatically.
  return false;
};

export interface PitchDeckAnalysis {
  strengths?: string[];
  areas_of_growth?: string[];
  narrative_clarity_1_5?: number;
  market_signal_1_5?: number;
  traction_evidence_1_5?: number;
  ask_clarity_1_5?: number;
  team_signal_1_5?: number;
  overall_band_1_5?: number;
  headline?: string;
  recommended_next_step?: string;
  model_used?: string;
  analyzed_at?: string;
}

interface Props {
  companyId: number;
  deckUrl: string | null;
  /** Backend truth-check: false when the deck URL points at our own
   *  /pitch-deck/file route but no file exists on disk behind it.
   *  null/undefined = unknown (older API response) — treat as ok. */
  deckAvailable?: boolean | null;
  analysis: PitchDeckAnalysis | null;
  analyzedAt: string | null;
  onReanalyze: () => Promise<void>;
  onUploaded?: () => void | Promise<void>;
}

const bandLabel = (b?: number) => {
  switch (b) {
    case 5: return { text: 'Exceptional', cls: 'bg-green-100 text-green-800' };
    case 4: return { text: 'Strong',      cls: 'bg-emerald-50 text-emerald-700' };
    case 3: return { text: 'Adequate',    cls: 'bg-yellow-50 text-yellow-700' };
    case 2: return { text: 'Weak',        cls: 'bg-orange-50 text-orange-700' };
    case 1: return { text: 'Critical gap',cls: 'bg-red-50 text-red-700' };
    default: return { text: 'Not scored', cls: 'bg-gray-100 text-gray-600' };
  }
};

const Bar: React.FC<{ label: string; value?: number }> = ({ label, value }) => {
  const v = typeof value === 'number' ? Math.max(0, Math.min(5, value)) : 0;
  const pct = (v / 5) * 100;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
        <span>{label}</span>
        <span className="font-medium text-gray-900">{value ?? '—'} / 5</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${v >= 4 ? 'bg-emerald-500' : v >= 3 ? 'bg-blue-500' : v >= 2 ? 'bg-amber-500' : 'bg-red-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const PitchDeckTab: React.FC<Props> = ({ companyId, deckUrl, deckAvailable, analysis, analyzedAt, onReanalyze, onUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [rehosting, setRehosting] = useState(false);

  const handleRehost = async () => {
    setRehosting(true);
    try {
      const res = await api.post(`/companies/${companyId}/pitch-deck/rehost`);
      const data = res.data;
      if (data?.ok && !data.noop) {
        toast.success('Deck downloaded and hosted locally — link is now permanent.');
        if (onUploaded) {
          await onUploaded();
        } else {
          window.location.reload();
        }
      } else if (data?.noop) {
        toast.success('Already hosted locally — no change needed.');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Rehost failed';
      toast.error(detail);
    } finally {
      setRehosting(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Pitch deck must be a PDF');
      return;
    }
    setUploading(true);
    try {
      const path = `/companies/${companyId}/pitch-deck/upload`;
      // The endpoint runs TWO inline Claude calls (Vision analysis +
      // brochure extraction) before responding, which can exceed the
      // default timeout on large decks. Give it a 4-min ceiling so a
      // slow-but-successful analysis isn't reported as a failed upload
      // (the file is saved server-side before analysis even starts).
      const cfg = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240_000,
      };
      // Build the FormData fresh per attempt — a consumed body can't be
      // re-sent if we fall back.
      const makeForm = () => {
        const fd = new FormData();
        fd.append('file', file);
        return fd;
      };
      // Prefer apiDirect (straight to Railway) to bypass Netlify's ~6 MB
      // proxy body cap. If that cross-origin call gets NO response
      // (CORS / preflight blocked — e.g. the page is served from an
      // origin not in the backend's allowlist, like a Netlify preview
      // URL), fall back to the same-origin proxied `api`. The proxy
      // always satisfies CORS but caps bodies at ~6 MB, so this only
      // helps decks under that size — which is the common case.
      let res;
      try {
        res = await apiDirect.post(path, makeForm(), cfg);
      } catch (directErr: any) {
        // Only a REAL server response (has an HTTP status) means "don't
        // retry". The apiDirect interceptor synthesizes a status-less
        // error.response for no-response/CORS failures — those SHOULD
        // fall back to the same-origin proxy (checking `.response` alone
        // here silently disabled the fallback).
        if (directErr?.response?.status) throw directErr;
        res = await api.post(path, makeForm(), cfg);
      }
      const band = res.data?.overall_band_1_5;
      toast.success(
        res.data?.analyzed
          ? `Pitch deck uploaded and analyzed${band ? ` (band ${band}/5)` : ''}.`
          : 'Pitch deck uploaded. Claude analysis is running (~30-60s) — refresh shortly to see results.',
      );
      if (onUploaded) {
        await onUploaded();
      } else {
        window.location.reload();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!deckUrl || deckAvailable === false) {
    return (
      <section className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <FileText className="w-5 h-5" />
          <span>No pitch deck on file for this company yet.</span>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {deckUrl && deckAvailable === false
            ? 'A deck link was recorded, but the file itself is missing from storage — upload a fresh PDF to fix it.'
            : 'Upload a PDF below, or import from a partner CSV with the deck URL in the column.'}
        </p>
        <div className="mt-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading & analyzing…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Upload pitch deck (PDF)
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Uploading triggers Claude Vision analysis automatically (~30-60s).
          </p>
        </div>
      </section>
    );
  }

  const overall = bandLabel(analysis?.overall_band_1_5);

  return (
    <div className="space-y-4">
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Pitch deck analysis
              </h2>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${overall.cls}`}>
                {overall.text} {analysis?.overall_band_1_5 ? `(${analysis.overall_band_1_5}/5)` : ''}
              </span>
            </div>
            {analysis?.headline && (
              <p className="text-sm text-gray-800 mt-2 leading-snug">{analysis.headline}</p>
            )}
            {analyzedAt && (
              <p className="text-[10px] text-gray-400 mt-1">
                Analyzed {new Date(analyzedAt).toLocaleString()}
                {analysis?.model_used && ` · ${analysis.model_used}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={deckUrl.includes('/pitch-deck/file') ? appendAuthToken(deckUrl) : deckUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50"
            >
              <ExternalLink className="w-3 h-3" /> Open deck
            </a>
            {isExternalAtRiskUrl(deckUrl) && (
              <button
                onClick={handleRehost}
                disabled={rehosting}
                title="Download this deck to our storage so the link doesn't expire"
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              >
                {rehosting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {rehosting ? 'Saving…' : 'Save & host'}
              </button>
            )}
            <button
              onClick={onReanalyze}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            >
              <RefreshCw className="w-3 h-3" /> Re-analyze
            </button>
          </div>
        </div>
        {isExternalAtRiskUrl(deckUrl) && (
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 leading-snug">
            <strong>Heads up:</strong> this deck is hosted on a partner CDN (likely Airtable)
            with a short-lived signed URL. If the Open Deck link returns an error, click
            <strong> Save &amp; host</strong> to download a permanent copy onto our servers.
          </p>
        )}
      </section>

      {analysis ? (
        <>
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
              Diagnostic bands
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <Bar label="Narrative clarity"   value={analysis.narrative_clarity_1_5} />
              <Bar label="Market signal"       value={analysis.market_signal_1_5} />
              <Bar label="Traction evidence"   value={analysis.traction_evidence_1_5} />
              <Bar label="Ask clarity"         value={analysis.ask_clarity_1_5} />
              <Bar label="Team signal"         value={analysis.team_signal_1_5} />
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <section className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-3 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Strengths
              </h3>
              {analysis.strengths && analysis.strengths.length > 0 ? (
                <ul className="space-y-2 text-sm text-gray-800">
                  {analysis.strengths.map((s, i) => (
                    <li key={i} className="leading-snug">• {s}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 italic">None surfaced.</p>
              )}
            </section>

            <section className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-3 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Areas of growth
              </h3>
              {analysis.areas_of_growth && analysis.areas_of_growth.length > 0 ? (
                <ul className="space-y-2 text-sm text-gray-800">
                  {analysis.areas_of_growth.map((s, i) => (
                    <li key={i} className="leading-snug">• {s}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 italic">None surfaced.</p>
              )}
            </section>
          </div>

          {analysis.recommended_next_step && (
            <section className="bg-indigo-50 text-indigo-900 text-sm p-4 rounded-lg leading-snug">
              <strong>Suggested next step:</strong> {analysis.recommended_next_step}
            </section>
          )}
        </>
      ) : (
        <section className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-sm text-gray-500">
            The deck is on file but hasn't been analyzed yet. Click <strong>Re-analyze</strong>
            to run Claude Vision over it (~30-60s).
          </p>
        </section>
      )}
    </div>
  );
};

export default PitchDeckTab;
